import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Paper,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PublishIcon from '@mui/icons-material/Publish';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import RefreshIcon from '@mui/icons-material/Refresh';
import useTheme from '../hooks/useTheme';

export default function Testing() {
  const theme = useTheme();
  const [testMode, setTestMode] = useState('discovery'); // 'discovery' or 'report'
  const [chatId, setChatId] = useState('user_hcrlweab4_mf847tj1');
  const [email, setEmail] = useState('');
  const [conversationIds, setConversationIds] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState('');
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [llm, setLlm] = useState('chatgpt');
  const [model, setModel] = useState('');
  const [temperature, setTemperature] = useState('0.1');
  const [testResult, setTestResult] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  
  // Multi-model testing state
  const [modelConfigs, setModelConfigs] = useState([
    { id: 1, llm: 'chatgpt', model: '', temperature: '0.1', isRunning: false, result: null }
  ]);
  const [isMultiModelRunning, setIsMultiModelRunning] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [modelAvailability, setModelAvailability] = useState({});
  
  // GPT-5 fallback detection
  const detectGPT5Fallback = (response, model) => {
    if (model.includes('gpt-5') && (response.includes('fallback') || 
        response.includes('GPT-4') || !response.includes('GPT-5'))) {
      return "GPT-5 temporarily unavailable, using GPT-4 instead";
    }
    return null;
  };

  // Show notification function
  const showNotification = (message, type = 'info') => {
    const notification = {
      id: Date.now(),
      message,
      type,
      timestamp: new Date()
    };
    setNotifications(prev => [...prev, notification]);
    
    // Auto-remove notification after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 5000);
  };

  // Enhanced error handling for different model types
  const handleModelError = (error, modelName) => {
    if (modelName.includes('claude')) {
      return "Claude API: Credit balance too low. Please contact support.";
    } else if (modelName.includes('gemini')) {
      return "Gemini API: Model overloaded. Please try again later.";
    } else if (modelName.includes('gpt-5')) {
      return "GPT-5 API: Service temporarily unavailable. Using GPT-4 instead.";
    } else if (modelName.includes('gpt-4')) {
      return "GPT-4 API: Rate limit exceeded. Please try again later.";
    } else {
      return `Error: ${error.message || 'Unknown error occurred'}`;
    }
  };

  // Check model status function
  const checkModelStatus = async () => {
    try {
      const response = await fetch('https://api.uxhq.com/api-health');
      if (response.ok) {
        const data = await response.json();
        return data.models || {};
      }
    } catch (error) {
      console.error('Failed to check model status:', error);
    }
    return null;
  };

  // Error logging function
  const logError = (error, context) => {
    console.error('Model Comparison Error:', {
      error: error.message,
      context: context,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    });
    
    // Show error notification to user
    showNotification(`Error: ${error.message}`, 'error');
  };

  // Retry function for failed models
  const retryModel = async (configId) => {
    const config = modelConfigs.find(c => c.id === configId);
    if (!config) return;

    // Mark as running
    setModelConfigs(prev => prev.map(c => 
      c.id === configId ? { ...c, isRunning: true, result: null } : c
    ));

    try {
      const requestBody = {
        conversation_id: selectedConversationId,
        llm_type: config.llm,
        model: config.model,
        temperature: parseFloat(config.temperature)
      };

      if (reportPrompt !== originalReportPrompt && reportPrompt.trim()) {
        requestBody.prompt = reportPrompt;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000);

      const response = await fetch('https://api.uxhq.com/generate_report_by_conversation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }

      const result = {
        conversationId: data.conversation_id,
        llmType: data.llm_type,
        model: data.model,
        temperature: data.temperature,
        htmlContent: data.html_content,
        message: data.message,
        inputTokens: data.input_tokens || 0,
        outputTokens: data.output_tokens || 0,
        error: false
      };

      // Check for GPT-5 fallback
      const fallbackMessage = detectGPT5Fallback(data.html_content || data.message, config.model);
      if (fallbackMessage) {
        showNotification(fallbackMessage, 'info');
      }

      setModelConfigs(prev => prev.map(c => 
        c.id === configId ? { ...c, result, isRunning: false } : c
      ));

    } catch (error) {
      logError(error, `Retry failed for ${config.llm}-${config.model}`);
      
      let errorMessage = handleModelError(error, config.model);
      if (error.name === 'AbortError') {
        errorMessage = 'Request timed out after 5 minutes. The model might be taking longer than expected.';
      } else if (error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      const errorResult = {
        error: true,
        message: `Error generating report: ${errorMessage}`,
        llmType: config.llm,
        model: config.model,
        temperature: config.temperature
      };

      setModelConfigs(prev => prev.map(c => 
        c.id === configId ? { ...c, result: errorResult, isRunning: false } : c
      ));
    }
  };
  
  // Production model configurations
  const [prodModelConfigs, setProdModelConfigs] = useState({
    discovery: null,
    report: null
  });
  const [isLoadingProdConfigs, setIsLoadingProdConfigs] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [showProdDialog, setShowProdDialog] = useState(false);
  const [deploymentHistory, setDeploymentHistory] = useState([]);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [sessionTimeout, setSessionTimeout] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [userId, setUserId] = useState(() => {
    // Get userId from localStorage or create new one
    const stored = localStorage.getItem('discovery_user_id');
    if (stored) return stored;
    const newId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('discovery_user_id', newId);
    return newId;
  });
  
  // Discovery prompt state
  const [discoveryPrompt, setDiscoveryPrompt] = useState('');
  const [originalDiscoveryPrompt, setOriginalDiscoveryPrompt] = useState('');
  const [isLoadingDiscoveryPrompt, setIsLoadingDiscoveryPrompt] = useState(false);
  const [showDiscoveryPromptEditor, setShowDiscoveryPromptEditor] = useState(false);
  
  // Report prompt state
  const [reportPrompt, setReportPrompt] = useState('');
  const [originalReportPrompt, setOriginalReportPrompt] = useState('');
  const [isLoadingReportPrompt, setIsLoadingReportPrompt] = useState(false);
  const [showReportPromptEditor, setShowReportPromptEditor] = useState(false);

  const handleRunTest = () => {
    if (testMode === 'discovery') {
      handleDiscoveryTest();
    } else {
      handleReportTest();
    }
  };

  // Multi-model configuration management
  const addModelConfig = () => {
    if (modelConfigs.length >= 3) return; // Limit to 3 models
    const newId = Math.max(...modelConfigs.map(c => c.id)) + 1;
    setModelConfigs(prev => [...prev, {
      id: newId,
      llm: 'chatgpt',
      model: '',
      temperature: '0.1',
      isRunning: false,
      result: null
    }]);
  };

  const removeModelConfig = (id) => {
    if (modelConfigs.length <= 1) return; // Keep at least one model
    setModelConfigs(prev => prev.filter(config => config.id !== id));
  };

  const updateModelConfig = (id, field, value) => {
    setModelConfigs(prev => prev.map(config => 
      config.id === id ? { ...config, [field]: value } : config
    ));
  };

  const runMultiModelTest = async () => {
    if (!selectedConversationId || !email) {
      alert('Please select a conversation ID first');
      return;
    }

    const validConfigs = modelConfigs.filter(config => config.model);
    if (validConfigs.length === 0) {
      alert('Please configure at least one model');
      return;
    }

    setIsMultiModelRunning(true);
    
    // Reset all results
    setModelConfigs(prev => prev.map(config => ({ ...config, result: null, isRunning: false })));

    // Run tests in parallel
    const testPromises = validConfigs.map(async (config) => {
      // Mark this config as running
      setModelConfigs(prev => prev.map(c => 
        c.id === config.id ? { ...c, isRunning: true } : c
      ));

      try {
        // Prepare request body - try without prompt first to see if that's the issue
        const requestBody = {
          conversation_id: selectedConversationId,
          llm_type: config.llm,
          model: config.model,
          temperature: parseFloat(config.temperature)
        };

        // Only add prompt if it was modified (backend might not support this parameter)
        if (reportPrompt !== originalReportPrompt && reportPrompt.trim()) {
          requestBody.prompt = reportPrompt;
        }

        console.log('Sending request for multi-model test:', requestBody);
        
        // Create an AbortController for timeout handling
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes timeout
        
        const response = await fetch('https://api.uxhq.com/generate_report_by_conversation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          // Try to get more detailed error information
          let errorMessage = `HTTP error! status: ${response.status}`;
          try {
            const errorData = await response.json();
            if (errorData.detail) {
              errorMessage = errorData.detail;
            } else if (errorData.error) {
              errorMessage = errorData.error;
            }
          } catch (e) {
            // If we can't parse the error response, use the status
            errorMessage = `HTTP error! status: ${response.status} - ${response.statusText}`;
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();
        
        if (data.error) {
          throw new Error(data.error);
        }

        const result = {
          conversationId: data.conversation_id,
          llmType: data.llm_type,
          model: data.model,
          temperature: data.temperature,
          htmlContent: data.html_content,
          message: data.message,
          inputTokens: data.input_tokens || 0,
          outputTokens: data.output_tokens || 0,
          error: false
        };

        // Check for GPT-5 fallback
        const fallbackMessage = detectGPT5Fallback(data.html_content || data.message, config.model);
        if (fallbackMessage) {
          showNotification(fallbackMessage, 'info');
        }

        // Update result for this config
        setModelConfigs(prev => prev.map(c => 
          c.id === config.id ? { ...c, result, isRunning: false } : c
        ));

      } catch (error) {
        logError(error, `Multi-model test failed for ${config.llm}-${config.model}`);
        
        let errorMessage = handleModelError(error, config.model);
        if (error.name === 'AbortError') {
          errorMessage = 'Request timed out after 5 minutes. The model might be taking longer than expected.';
        } else if (error.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        }
        
        const errorResult = {
          error: true,
          message: `Error generating report: ${errorMessage}`,
          llmType: config.llm,
          model: config.model,
          temperature: config.temperature
        };

        setModelConfigs(prev => prev.map(c => 
          c.id === config.id ? { ...c, result: errorResult, isRunning: false } : c
        ));
      }
    });

    await Promise.all(testPromises);
    setIsMultiModelRunning(false);
  };

  const runSingleModelTest = async (configId) => {
    console.log('runSingleModelTest called with configId:', configId);
    const config = modelConfigs.find(c => c.id === configId);
    console.log('Found config:', config);
    
    if (!config || !config.model) {
      console.log('No config or model found, returning');
      return;
    }

    console.log('Starting single model test for:', config.llm, config.model);
    
    // Mark this config as running
    setModelConfigs(prev => prev.map(c => 
      c.id === configId ? { ...c, isRunning: true } : c
    ));

    try {
      // Prepare request body - try without prompt first to see if that's the issue
      const requestBody = {
        conversation_id: selectedConversationId,
        llm_type: config.llm,
        model: config.model,
        temperature: parseFloat(config.temperature)
      };

      // Only add prompt if it was modified (backend might not support this parameter)
      if (reportPrompt !== originalReportPrompt && reportPrompt.trim()) {
        requestBody.prompt = reportPrompt;
      }

      console.log('Sending request for single model test:', requestBody);

      // Create an AbortController for timeout handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes timeout

      const response = await fetch('https://api.uxhq.com/generate_report_by_conversation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      console.log('Response received:', response.status, response.statusText);

      if (!response.ok) {
        // Try to get more detailed error information
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (e) {
          // If we can't parse the error response, use the status
          errorMessage = `HTTP error! status: ${response.status} - ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Response data:', data);
      
      if (data.error) {
        console.log('Error in response data:', data.error);
        throw new Error(data.error);
      }

      const result = {
        conversationId: data.conversation_id,
        llmType: data.llm_type,
        model: data.model,
        temperature: data.temperature,
        htmlContent: data.html_content,
        message: data.message,
        inputTokens: data.input_tokens || 0,
        outputTokens: data.output_tokens || 0,
        error: false
      };

      // Check for GPT-5 fallback
      const fallbackMessage = detectGPT5Fallback(data.html_content || data.message, config.model);
      if (fallbackMessage) {
        showNotification(fallbackMessage, 'info');
      }

      // Update result for this config
      setModelConfigs(prev => prev.map(c => 
        c.id === configId ? { ...c, result, isRunning: false } : c
      ));

    } catch (error) {
      logError(error, `Single model test failed for ${config.llm}-${config.model}`);
      
      let errorMessage = handleModelError(error, config.model);
      if (error.name === 'AbortError') {
        errorMessage = 'Request timed out after 5 minutes. The model might be taking longer than expected.';
      } else if (error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      const errorResult = {
        error: true,
        message: `Error generating report: ${errorMessage}`,
        llmType: config.llm,
        model: config.model,
        temperature: config.temperature
      };

      setModelConfigs(prev => prev.map(c => 
        c.id === configId ? { ...c, result: errorResult, isRunning: false } : c
      ));
    }
  };

  const fetchConversationIdsByEmail = async (userEmail) => {
    if (!userEmail.trim()) {
      setConversationIds([]);
      setSelectedConversationId('');
      return;
    }

    setIsLoadingConversations(true);
    try {
      const response = await fetch(`https://api.uxhq.com/conversation_ids_by_email?email=${encodeURIComponent(userEmail)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }

      setConversationIds(data.conversation_ids || []);
      setSelectedConversationId(''); // Reset selection when new email is entered
      
      console.log(`Found ${data.total_conversations} conversations for email: ${userEmail}`);
    } catch (error) {
      console.error('Error fetching conversation IDs:', error);
      setConversationIds([]);
      setSelectedConversationId('');
      alert(`Error fetching conversations: ${error.message}`);
    } finally {
      setIsLoadingConversations(false);
    }
  };

  const handleEmailChange = (newEmail) => {
    setEmail(newEmail);
    setSelectedConversationId(''); // Reset selection when email changes
  };

  // Debounced effect for fetching conversation IDs
  React.useEffect(() => {
    if (!email.trim()) {
      setConversationIds([]);
      setSelectedConversationId('');
      return;
    }

    const timeoutId = setTimeout(() => {
      fetchConversationIdsByEmail(email);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [email]);

  const handleDiscoveryTest = async () => {
    // Reset session timeout on activity
    resetSessionTimeout();
    
    setIsRunning(true);
    try {
      // Check if prompt was modified
      const promptToSend = discoveryPrompt !== originalDiscoveryPrompt ? discoveryPrompt : 'none';
      
      const response = await fetch('https://api.uxhq.com/chat-test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          user_input: currentMessage || "Hello, let's start our conversation",
          model: llm,
          llm_model: model,
          temperature: parseFloat(temperature),
          prompt: promptToSend
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }

      // Add user message if it's not empty
      if (currentMessage.trim()) {
        setChatMessages(prev => [...prev, { role: 'user', content: currentMessage }]);
        setCurrentMessage('');
      }

      // Add bot response
      setChatMessages(prev => [...prev, { role: 'assistant', content: data.bot_message }]);
    } catch (error) {
      console.error('Error in discovery chat:', error);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Sorry, I encountered an error: ${error.message}` 
      }]);
    } finally {
      setIsRunning(false);
    }
  };

  const handleReportTest = async () => {
    if (!selectedConversationId) {
      alert('Please select a conversation ID first');
      return;
    }

    setIsRunning(true);
    try {
      // Check if prompt was modified
      const promptToSend = reportPrompt !== originalReportPrompt ? reportPrompt : 'none';
      
      const response = await fetch('https://api.uxhq.com/generate_report_by_conversation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversation_id: selectedConversationId,
          llm_type: llm,
          model: model,
          temperature: parseFloat(temperature),
          prompt: promptToSend
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }

      setTestResult({
        conversationId: data.conversation_id,
        llmType: data.llm_type,
        model: data.model,
        temperature: data.temperature,
        htmlContent: data.html_content,
        message: data.message,
        inputTokens: data.input_tokens || 0,
        outputTokens: data.output_tokens || 0
      });
    } catch (error) {
      console.error('Error generating report:', error);
      setTestResult({
        error: true,
        message: `Error generating report: ${error.message}`
      });
    } finally {
      setIsRunning(false);
    }
  };

  const handleSendMessage = async () => {
    if (!currentMessage.trim()) return;
    
    // Reset session timeout on activity
    resetSessionTimeout();
    
    // Store the message to send
    const messageToSend = currentMessage.trim();
    
    // Clear input immediately for better UX
    setCurrentMessage('');
    
    // Add user message immediately
    setChatMessages(prev => [...prev, { role: 'user', content: messageToSend }]);
    
    setIsRunning(true);
    try {
      // Check if prompt was modified
      const promptToSend = discoveryPrompt !== originalDiscoveryPrompt ? discoveryPrompt : 'none';
      
      const response = await fetch('https://api.uxhq.com/chat-test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          user_input: messageToSend,
          model: llm,
          llm_model: model,
          temperature: parseFloat(temperature),
          prompt: promptToSend
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }

      // Add bot response
      setChatMessages(prev => [...prev, { role: 'assistant', content: data.bot_message }]);
    } catch (error) {
      console.error('Error sending message:', error);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Sorry, I encountered an error: ${error.message}` 
      }]);
    } finally {
      setIsRunning(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Cost calculation based on model pricing (per 1M tokens)
  const calculateCost = (model, llmType, inputTokens, outputTokens) => {
    // Pricing per 1M tokens (USD) - [input, output]
    // Source: Official API pricing pages and verified rates
    const pricing = {
      'chatgpt': {
        'gpt-5': [2.50, 10.00],              // Estimated based on GPT-4 Turbo pricing
        'gpt-4o': [5.00, 15.00],             // OpenAI official pricing
        'gpt-4o-mini': [0.15, 0.60],         // OpenAI official pricing
        'gpt-4.1': [2.00, 8.00],             // Zapier/verified source
        'gpt-4': [30.00, 60.00],             // OpenAI official pricing
        'gpt-4-turbo': [10.00, 30.00],       // OpenAI official pricing
        'gpt-4.1-mini': [0.40, 1.60],        // Zapier/verified source
        'gpt-4.1-nano': [0.10, 0.40],        // Zapier/verified source
        'gpt-5-nano': [0.10, 0.40],          // Estimated similar to 4.1-nano
        'gpt-5-mini': [0.40, 1.60],          // Estimated similar to 4.1-mini
      },
      'claude': {
        'claude-opus-4-1-20250805': [15.00, 75.00],        // Anthropic official pricing
        'claude-opus-4-1': [15.00, 75.00],                 // Anthropic official pricing
        'claude-opus-4-20250514': [15.00, 75.00],          // Anthropic official pricing
        'claude-opus-4-0': [15.00, 75.00],                 // Anthropic official pricing
        'claude-sonnet-4-5-20250929': [3.00, 15.00],       // Anthropic official pricing
        'claude-sonnet-4-5': [3.00, 15.00],                // Anthropic official pricing
        'claude-sonnet-4-20250514': [3.00, 15.00],         // Anthropic official pricing
        'claude-sonnet-4-0': [3.00, 15.00],                // Anthropic official pricing
        'claude-3-7-sonnet-20250219': [3.00, 15.00],       // Anthropic official pricing
        'claude-3-7-sonnet-latest': [3.00, 15.00],         // Anthropic official pricing
        'claude-3-5-haiku-20241022': [1.00, 5.00],         // Anthropic official pricing
        'claude-3-5-haiku-latest': [1.00, 5.00],           // Anthropic official pricing
        'claude-3-5-sonnet-20241022': [3.00, 15.00],       // Anthropic official pricing
        'claude-3-5-sonnet-20240620': [3.00, 15.00],       // Anthropic official pricing
        'claude-3-opus-20240229': [15.00, 75.00],          // Anthropic official pricing
        'claude-3-opus-latest': [15.00, 75.00],            // Anthropic official pricing
        'claude-3-haiku-20240307': [0.25, 1.25],           // Anthropic official pricing
        'default': [3.00, 15.00]
      },
      'gemini': {
        'gemini-2.5-pro': [1.25, 10.00],       // Monica API / verified source
        'gemini-2.5-flash': [0.10, 0.40],      // Estimated based on Flash-Lite
        'gemini-2.5-flash-lite': [0.10, 0.40]  // Monica API / verified source
      }
    };

    const modelPricing = pricing[llmType]?.[model] || pricing[llmType]?.['default'] || [3.00, 15.00];
    const inputCost = (inputTokens / 1000000) * modelPricing[0];
    const outputCost = (outputTokens / 1000000) * modelPricing[1];
    
    return {
      inputCost,
      outputCost,
      totalCost: inputCost + outputCost
    };
  };

  // Format cost for display - handles very small amounts
  const formatCost = (cost) => {
    if (cost === 0) return '$0.00';
    if (cost < 0.001) return `$${cost.toFixed(6)}`; // Show 6 decimals for very small amounts
    if (cost < 0.01) return `$${cost.toFixed(4)}`;  // Show 4 decimals for small amounts
    return `$${cost.toFixed(2)}`;                    // Show 2 decimals for larger amounts
  };

  // Fetch discovery prompt
  const fetchDiscoveryPrompt = async () => {
    setIsLoadingDiscoveryPrompt(true);
    try {
      const response = await fetch('https://api.uxhq.com/prompts', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.prompt) {
        setDiscoveryPrompt(data.prompt);
        setOriginalDiscoveryPrompt(data.prompt);
      }
    } catch (error) {
      console.error('Error fetching discovery prompt:', error);
    } finally {
      setIsLoadingDiscoveryPrompt(false);
    }
  };

  // Fetch report prompt
  const fetchReportPrompt = async () => {
    setIsLoadingReportPrompt(true);
    try {
      const response = await fetch('https://api.uxhq.com/report_prompts', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.prompt) {
        setReportPrompt(data.prompt);
        setOriginalReportPrompt(data.prompt);
      }
    } catch (error) {
      console.error('Error fetching report prompt:', error);
    } finally {
      setIsLoadingReportPrompt(false);
    }
  };

  const exportComparisonResults = () => {
    const results = modelConfigs
      .filter(config => config.result)
      .map(config => ({
        model: `${config.result.llmType} - ${config.result.model}`,
        temperature: config.result.temperature,
        conversationId: config.result.conversationId,
        htmlContent: config.result.htmlContent,
        message: config.result.message,
        error: config.result.error,
        timestamp: new Date().toISOString()
      }));

    const exportData = {
      conversationId: selectedConversationId,
      email: email,
      timestamp: new Date().toISOString(),
      results: results
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], {type: 'application/json'});
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `model-comparison-${selectedConversationId}-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Fetch production model configurations
  const fetchProdModelConfigs = async () => {
    setIsLoadingProdConfigs(true);
    try {
      const response = await fetch('https://api.uxhq.com/model_configs', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.discovery && data.report) {
        setProdModelConfigs({
          discovery: data.discovery,
          report: data.report
        });
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching production model configs:', error);
      // Don't show alert for this, just log the error
    } finally {
      setIsLoadingProdConfigs(false);
    }
  };

  const handleUseModelOnProd = () => {
    setShowProdDialog(true);
  };

  const handleConfirmProd = async () => {
    try {
      setIsRunning(true);
      
      const response = await fetch('https://api.uxhq.com/save_model_config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: testMode,
          llm: llm,
          model: model,
          temperature: parseFloat(temperature)
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status === 'success') {
        console.log('Model configuration saved successfully:', data);
        
        // Save to deployment history
        const historyEntry = {
          id: Date.now(),
          type: testMode,
          llm: llm,
          model: model,
          temperature: parseFloat(temperature),
          timestamp: new Date().toISOString(),
          date: new Date().toLocaleDateString(),
          time: new Date().toLocaleTimeString(),
          filename: data.filename
        };
        saveDeploymentHistory(historyEntry);
        
        // You can add a success notification here
      } else {
        throw new Error(data.message || 'Failed to save model configuration');
      }
    } catch (error) {
      console.error('Error saving model configuration:', error);
      alert(`Error saving model configuration: ${error.message}`);
    } finally {
      setIsRunning(false);
      setShowProdDialog(false);
    }
  };

  const handleDiscardProd = () => {
    setShowProdDialog(false);
  };

  // Load deployment history from localStorage
  const loadDeploymentHistory = () => {
    try {
      const stored = localStorage.getItem('deployment_history');
      if (stored) {
        const history = JSON.parse(stored);
        setDeploymentHistory(history);
      } else {
        setDeploymentHistory([]);
      }
    } catch (error) {
      console.error('Error loading deployment history:', error);
      setDeploymentHistory([]);
    }
  };

  // Save deployment history to localStorage
  const saveDeploymentHistory = (newEntry) => {
    try {
      const updatedHistory = [newEntry, ...deploymentHistory].slice(0, 20); // Keep last 20 entries
      setDeploymentHistory(updatedHistory);
      localStorage.setItem('deployment_history', JSON.stringify(updatedHistory));
    } catch (error) {
      console.error('Error saving deployment history:', error);
    }
  };

  // Load history and production configs on component mount
  React.useEffect(() => {
    loadDeploymentHistory();
    fetchProdModelConfigs();
    fetchDiscoveryPrompt();
    fetchReportPrompt();
    
    // Check model status on component mount
    const updateModelStatus = async () => {
      const status = await checkModelStatus();
      if (status) {
        setModelAvailability(status);
      }
    };
    updateModelStatus();
  }, []);

  // Cleanup timeout on component unmount
  React.useEffect(() => {
    return () => {
      if (sessionTimeout) {
        clearTimeout(sessionTimeout);
      }
    };
  }, [sessionTimeout]);

  const clearChat = () => {
    // Clear chat messages
    setChatMessages([]);
    
    // Remove current userId from localStorage
    localStorage.removeItem('discovery_user_id');
    
    // Create new userId
    const newId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('discovery_user_id', newId);
    setUserId(newId);
  };

  const startSession = () => {
    // Clear any existing timeout
    if (sessionTimeout) {
      clearTimeout(sessionTimeout);
    }

    // Create new userId for this session
    const newId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('discovery_user_id', newId);
    setUserId(newId);

    // Clear previous chat messages and add welcome message
    setChatMessages([{ 
      role: 'assistant', 
      content: 'Hi! What is your name?' 
    }]);

    // Set session as active
    setIsSessionActive(true);
    setSessionStartTime(new Date());

    // Set 30-minute timeout
    const timeout = setTimeout(() => {
      stopSession();
    }, 30 * 60 * 1000); // 30 minutes

    setSessionTimeout(timeout);

    console.log('Session started with userId:', newId);
  };

  const stopSession = () => {
    // Clear timeout
    if (sessionTimeout) {
      clearTimeout(sessionTimeout);
      setSessionTimeout(null);
    }

    // Set session as inactive
    setIsSessionActive(false);
    setSessionStartTime(null);

    // Clear chat messages
    setChatMessages([]);

    // Remove userId from localStorage
    localStorage.removeItem('discovery_user_id');

    console.log('Session stopped');
  };

  const resetSessionTimeout = () => {
    if (isSessionActive && sessionTimeout) {
      // Clear existing timeout
      clearTimeout(sessionTimeout);
      
      // Set new 30-minute timeout
      const timeout = setTimeout(() => {
        stopSession();
      }, 30 * 60 * 1000);
      
      setSessionTimeout(timeout);
    }
  };

  return (
    <Box sx={{ 
      p: 3, 
      background: theme.backgroundColor, 
      minHeight: '100vh',
      color: theme.textColor 
    }}>
      {/* Notifications */}
      {notifications.length > 0 && (
        <Box sx={{ 
          position: 'fixed', 
          top: 20, 
          right: 20, 
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}>
          {notifications.map(notification => (
            <Box
              key={notification.id}
              sx={{
                background: notification.type === 'error' ? '#ffebee' : '#e3f2fd',
                color: notification.type === 'error' ? '#c62828' : '#1976d2',
                padding: 2,
                borderRadius: 1,
                border: `1px solid ${notification.type === 'error' ? '#c62828' : '#1976d2'}`,
                maxWidth: 400,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                animation: 'slideIn 0.3s ease-out'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {notification.message}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      
      {/* Header */}
      <Box sx={{ mb: 4, pt: 10 }}>
        <Typography variant="h3" sx={{ 
          fontWeight: 'bold', 
          color: theme.textColor || '#000000',
          mb: 3,
          lineHeight: 1.2,
          fontSize: '2.5rem',
          display: 'block'
        }}>
          MODEL TESTING
        </Typography>
        <Typography variant="body1" sx={{ 
          color: theme.textSecondaryColor || '#666666',
          fontSize: '18px',
          lineHeight: 1.6,
          mt: 2,
          display: 'block'
        }}>
          Test and evaluate different language models with custom parameters
        </Typography>
      </Box>

      {/* Test Mode Buttons */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Button
          variant={testMode === 'discovery' ? 'contained' : 'outlined'}
          onClick={() => setTestMode('discovery')}
          sx={{
            background: testMode === 'discovery' ? theme.primaryColor : 'transparent',
            color: testMode === 'discovery' ? 'white' : theme.primaryColor,
            borderColor: theme.primaryColor,
            fontWeight: 600,
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '16px',
            '&:hover': {
              background: testMode === 'discovery' ? theme.primaryColor : theme.primaryColor + '10',
              borderColor: theme.primaryColor,
            },
          }}
        >
          Test Discovery Chat
        </Button>
        <Button
          variant={testMode === 'report' ? 'contained' : 'outlined'}
          onClick={() => setTestMode('report')}
          sx={{
            background: testMode === 'report' ? theme.primaryColor : 'transparent',
            color: testMode === 'report' ? 'white' : theme.primaryColor,
            borderColor: theme.primaryColor,
            fontWeight: 600,
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '16px',
            '&:hover': {
              background: testMode === 'report' ? theme.primaryColor : theme.primaryColor + '10',
              borderColor: theme.primaryColor,
            },
          }}
        >
          Test Report Generation
        </Button>
      </Box>

      {/* Current Production Model Configuration */}
      <Card sx={{ 
        mb: 3, 
        background: theme.cardBackground,
        border: `1px solid ${theme.borderColor}`,
        boxShadow: '0 4px 24px 0 rgba(30,34,40,0.08)'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 600, 
              color: theme.textColor 
            }}>
              Current Production Configuration
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={fetchProdModelConfigs}
              disabled={isLoadingProdConfigs}
              sx={{
                borderColor: theme.primaryColor,
                color: theme.primaryColor,
                '&:hover': {
                  borderColor: theme.primaryColor,
                  background: `${theme.primaryColor}10`,
                },
                '&:disabled': {
                  borderColor: theme.textSecondaryColor,
                  color: theme.textSecondaryColor,
                },
              }}
            >
              {isLoadingProdConfigs ? 'Loading...' : 'Refresh'}
            </Button>
          </Box>

          {isLoadingProdConfigs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Box sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: theme.primaryColor,
                animation: 'pulse 1.5s infinite'
              }} />
            </Box>
          ) : (
            <Box>
              {/* Discovery Configuration - Show only in Discovery mode */}
              {testMode === 'discovery' && (
                <Box sx={{ 
                  p: 2, 
                  background: theme.backgroundColor,
                  borderRadius: 2,
                  border: `1px solid ${theme.borderColor}`
                }}>
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 600, 
                    color: theme.textColor,
                    mb: 1
                  }}>
                    Discovery Model
                  </Typography>
                  {prodModelConfigs.discovery ? (
                    <Box>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Chip 
                          label={prodModelConfigs.discovery.llm?.toUpperCase() || 'N/A'}
                          size="small"
                          sx={{ 
                            background: '#e3f2fd',
                            color: '#1976d2',
                            fontWeight: 500
                          }}
                        />
                        <Chip 
                          label={prodModelConfigs.discovery.model || 'N/A'}
                          size="small"
                          sx={{ 
                            background: '#f3e5f5',
                            color: '#7b1fa2',
                            fontWeight: 500
                          }}
                        />
                        <Chip 
                          label={`Temp: ${prodModelConfigs.discovery.temperature || 'N/A'}`}
                          size="small"
                          sx={{ 
                            background: '#fff3e0',
                            color: '#f57c00',
                            fontWeight: 500
                          }}
                        />
                      </Box>
                      {prodModelConfigs.discovery.created_at && (
                        <Typography variant="caption" sx={{ 
                          color: theme.textSecondaryColor,
                          fontSize: '0.7rem'
                        }}>
                          Deployed: {new Date(prodModelConfigs.discovery.created_at).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: theme.textSecondaryColor }}>
                      No discovery model configuration found
                    </Typography>
                  )}
                </Box>
              )}

              {/* Report Configuration - Show only in Report mode */}
            {testMode === 'report' && (
                <Box sx={{ 
                  p: 2, 
                  background: theme.backgroundColor,
                  borderRadius: 2,
                  border: `1px solid ${theme.borderColor}`
                }}>
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 600, 
                    color: theme.textColor,
                    mb: 1
                  }}>
                    Report Model
                  </Typography>
                  {prodModelConfigs.report ? (
                    <Box>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Chip 
                          label={prodModelConfigs.report.llm?.toUpperCase() || 'N/A'}
                          size="small"
                          sx={{ 
                            background: '#e3f2fd',
                            color: '#1976d2',
                            fontWeight: 500
                          }}
                        />
                        <Chip 
                          label={prodModelConfigs.report.model || 'N/A'}
                          size="small"
                          sx={{ 
                            background: '#f3e5f5',
                            color: '#7b1fa2',
                            fontWeight: 500
                          }}
                        />
                        <Chip 
                          label={`Temp: ${prodModelConfigs.report.temperature || 'N/A'}`}
                          size="small"
                          sx={{ 
                            background: '#fff3e0',
                            color: '#f57c00',
                            fontWeight: 500
                          }}
                        />
                      </Box>
                      {prodModelConfigs.report.created_at && (
                        <Typography variant="caption" sx={{ 
                          color: theme.textSecondaryColor,
                          fontSize: '0.7rem'
                        }}>
                          Deployed: {new Date(prodModelConfigs.report.created_at).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: theme.textSecondaryColor }}>
                      No report model configuration found
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Report Prompt Editor */}
      {testMode === 'report' && (
        <Card sx={{ 
          mb: 3, 
          background: theme.cardBackground,
          border: `1px solid ${theme.borderColor}`,
          boxShadow: '0 4px 24px 0 rgba(30,34,40,0.08)'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: theme.textColor 
              }}>
                Report Generation Prompt
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={fetchReportPrompt}
                  disabled={isLoadingReportPrompt}
                  sx={{
                    borderColor: theme.primaryColor,
                    color: theme.primaryColor,
                    '&:hover': {
                      borderColor: theme.primaryColor,
                      background: `${theme.primaryColor}10`,
                    },
                    '&:disabled': {
                      borderColor: theme.textSecondaryColor,
                      color: theme.textSecondaryColor,
                    },
                  }}
                >
                  {isLoadingReportPrompt ? 'Loading...' : 'Refresh'}
                </Button>
                <Button
                  variant={showReportPromptEditor ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setShowReportPromptEditor(!showReportPromptEditor)}
                  sx={{
                    background: showReportPromptEditor ? theme.primaryColor : 'transparent',
                    color: showReportPromptEditor ? 'white' : theme.primaryColor,
                    borderColor: theme.primaryColor,
                    '&:hover': {
                      background: showReportPromptEditor ? theme.primaryColor : `${theme.primaryColor}10`,
                      borderColor: theme.primaryColor,
                    },
                  }}
                >
                  {showReportPromptEditor ? 'Hide Editor' : 'Edit Prompt'}
                </Button>
                {reportPrompt !== originalReportPrompt && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setReportPrompt(originalReportPrompt)}
                    sx={{
                      borderColor: '#f57c00',
                      color: '#f57c00',
                      '&:hover': {
                        borderColor: '#f57c00',
                        background: '#fff3e010',
                      },
                    }}
                  >
                    Reset
                  </Button>
                )}
              </Box>
            </Box>

            {reportPrompt !== originalReportPrompt && (
              <Box sx={{ 
                mb: 2, 
                p: 1.5, 
                background: '#fff3e0', 
                borderRadius: 1,
                border: '1px solid #f57c00'
              }}>
                <Typography variant="body2" sx={{ color: '#f57c00', fontWeight: 500 }}>
                  ⚠ Prompt has been modified. This custom prompt will be used for report generation.
                </Typography>
              </Box>
            )}

            {showReportPromptEditor ? (
              <TextField
                fullWidth
                multiline
                rows={12}
                value={reportPrompt}
                onChange={(e) => setReportPrompt(e.target.value)}
                placeholder="Report generation prompt will appear here..."
                variant="outlined"
                disabled={isLoadingReportPrompt}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    color: theme.textColor,
                    '& fieldset': {
                      borderColor: theme.borderColor,
                    },
                    '&:hover fieldset': {
                      borderColor: theme.primaryColor,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.primaryColor,
                    },
                  },
                }}
              />
            ) : (
              <Box sx={{ 
                p: 2, 
                background: theme.backgroundColor,
                borderRadius: 1,
                border: `1px solid ${theme.borderColor}`,
                maxHeight: 200,
                overflow: 'auto'
              }}>
                {isLoadingReportPrompt ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <Box sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: theme.primaryColor,
                      animation: 'pulse 1.5s infinite'
                    }} />
                  </Box>
                ) : reportPrompt ? (
                  <Typography variant="body2" sx={{ 
                    color: theme.textColor,
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    lineHeight: 1.6
                  }}>
                    {reportPrompt.substring(0, 500)}
                    {reportPrompt.length > 500 && '...'}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ color: theme.textSecondaryColor, fontStyle: 'italic' }}>
                    No prompt loaded. Click "Refresh" to load the report generation prompt.
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Control Panel */}
      <Card sx={{ 
        mb: 3, 
        background: theme.cardBackground,
        border: `1px solid ${theme.borderColor}`,
        boxShadow: '0 4px 24px 0 rgba(30,34,40,0.08)'
      }}>
        <CardContent sx={{ p: 3 }}>
          {testMode === 'report' ? (
            // Multi-model configuration for report mode
            <Box>
              {/* Email and Conversation Selection */}
              <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <TextField
                      value={email}
                      onChange={(e) => handleEmailChange(e.target.value)}
                      variant="outlined"
                      size="small"
                      label="Email"
                      InputProps={{
                        endAdornment: isLoadingConversations && email ? (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            color: theme.textSecondaryColor,
                            fontSize: '0.8rem'
                          }}>
                            <Box sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              background: theme.primaryColor,
                              animation: 'pulse 1.5s infinite'
                            }} />
                            Loading...
                          </Box>
                        ) : null
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: theme.textColor,
                          '& fieldset': {
                            borderColor: theme.borderColor,
                          },
                          '&:hover fieldset': {
                            borderColor: theme.primaryColor,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.primaryColor,
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: theme.textSecondaryColor,
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: theme.primaryColor,
                        },
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <Select
                      value={selectedConversationId}
                      onChange={(e) => setSelectedConversationId(e.target.value)}
                      size="small"
                      displayEmpty
                      disabled={isLoadingConversations || conversationIds.length === 0}
                      renderValue={(selected) => {
                        if (!selected) {
                          if (isLoadingConversations) {
                            return <span style={{ color: theme.textSecondaryColor }}>Loading...</span>;
                          }
                          if (conversationIds.length === 0 && email) {
                            return <span style={{ color: theme.textSecondaryColor }}>No conversations found</span>;
                          }
                          return <span style={{ color: theme.textSecondaryColor }}>Select Conversation</span>;
                        }
                        return selected;
                      }}
                      sx={{
                        color: theme.textColor,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.borderColor,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.primaryColor,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.primaryColor,
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select Conversation</em>
                      </MenuItem>
                      {conversationIds.map((convId, index) => (
                        <MenuItem key={convId} value={convId}>
                          {convId} {index < 5 && <span style={{ color: theme.textSecondaryColor, fontSize: '0.8rem' }}>(#{index + 1})</span>}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Button
                    variant="contained"
                    onClick={runMultiModelTest}
                    disabled={isMultiModelRunning || !email || !selectedConversationId || modelConfigs.every(c => !c.model)}
                    startIcon={<CompareArrowsIcon />}
                    fullWidth
                    sx={{
                      background: theme.primaryColor,
                      color: 'white',
                      fontWeight: 600,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '16px',
                      '&:hover': {
                        background: theme.primaryColor,
                        opacity: 0.9,
                      },
                      '&:disabled': {
                        background: theme.textSecondaryColor,
                      },
                    }}
                  >
                    {isMultiModelRunning ? 'RUNNING...' : 'RUN TEST'}
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      // Use the first model configuration for production
                      const firstModelConfig = modelConfigs[0];
                      if (firstModelConfig && firstModelConfig.model) {
                        setLlm(firstModelConfig.llm);
                        setModel(firstModelConfig.model);
                        setTemperature(firstModelConfig.temperature);
                        handleUseModelOnProd();
                      } else {
                        alert('Please configure at least one model first');
                      }
                    }}
                    disabled={isRunning || modelConfigs.every(c => !c.model)}
                    startIcon={<PublishIcon />}
                    fullWidth
                    sx={{
                      borderColor: theme.primaryColor,
                      color: theme.primaryColor,
                      fontWeight: 600,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '16px',
                      '&:hover': {
                        borderColor: theme.primaryColor,
                        background: `${theme.primaryColor}10`,
                      },
                      '&:disabled': {
                        borderColor: theme.textSecondaryColor,
                        color: theme.textSecondaryColor,
                      },
                    }}
                  >
                    USE FIRST MODEL ON PROD
                  </Button>
                </Grid>
              </Grid>

              {/* Model Configurations */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 600, 
                    color: theme.textColor 
                  }}>
                    Model Configurations ({modelConfigs.length}/3)
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={addModelConfig}
                    disabled={modelConfigs.length >= 3}
                    startIcon={<AddIcon />}
                    sx={{
                      borderColor: theme.primaryColor,
                      color: theme.primaryColor,
                      '&:hover': {
                        borderColor: theme.primaryColor,
                        background: `${theme.primaryColor}10`,
                      },
                      '&:disabled': {
                        borderColor: theme.textSecondaryColor,
                        color: theme.textSecondaryColor,
                      },
                    }}
                  >
                    Add Model
                  </Button>
                </Box>

                {/* Table-like Configuration Layout */}
                <Box sx={{ 
                  background: 'white',
                  border: `1px solid ${theme.borderColor}`,
                  borderRadius: 1,
                  overflow: 'hidden'
                }}>
                  {/* Table Headers */}
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr 1fr 120px 40px',
                    background: theme.backgroundColor,
                    borderBottom: `1px solid ${theme.borderColor}`
                  }}>
                    <Box sx={{ p: 2, borderRight: `1px solid ${theme.borderColor}` }}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 600, 
                        color: theme.textColor,
                        fontSize: '0.9rem'
                      }}>
                        LLM
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2, borderRight: `1px solid ${theme.borderColor}` }}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 600, 
                        color: theme.textColor,
                        fontSize: '0.9rem'
                      }}>
                        Model
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2, borderRight: `1px solid ${theme.borderColor}` }}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 600, 
                        color: theme.textColor,
                        fontSize: '0.9rem'
                      }}>
                        Temperature
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2, borderRight: `1px solid ${theme.borderColor}` }}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 600, 
                        color: theme.textColor,
                        fontSize: '0.9rem'
                      }}>
                        Deploy
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      {/* Empty header for remove button column */}
                    </Box>
                  </Box>

                  {/* Configuration Rows */}
                  {modelConfigs.map((config, index) => (
                    <Box 
                      key={config.id}
                      sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr 1fr 1fr 120px 40px',
                        borderBottom: index < modelConfigs.length - 1 ? `1px solid ${theme.borderColor}` : 'none',
                        position: 'relative'
                      }}
                    >
                      {/* LLM Column */}
                      <Box sx={{ p: 2, borderRight: `1px solid ${theme.borderColor}` }}>
                        <FormControl fullWidth size="small">
                          <Select
                            value={config.llm}
                            onChange={(e) => updateModelConfig(config.id, 'llm', e.target.value)}
                            sx={{
                              color: theme.textColor,
                              fontSize: '0.9rem',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.borderColor,
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.primaryColor,
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.primaryColor,
                              },
                            }}
                          >
                            <MenuItem value="chatgpt">ChatGPT</MenuItem>
                            <MenuItem value="gemini">Gemini</MenuItem>
                            <MenuItem value="claude">Claude</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      {/* Model Column */}
                      <Box sx={{ p: 2, borderRight: `1px solid ${theme.borderColor}` }}>
                        <FormControl fullWidth size="small">
                          <Select
                            value={config.model}
                            onChange={(e) => updateModelConfig(config.id, 'model', e.target.value)}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected) {
                                return <span style={{ color: theme.textSecondaryColor, fontSize: '0.9rem' }}>Select Model</span>;
                              }
                              return <span style={{ fontSize: '0.9rem' }}>{selected}</span>;
                            }}
                            sx={{
                              color: theme.textColor,
                              fontSize: '0.9rem',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.borderColor,
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.primaryColor,
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.primaryColor,
                              },
                            }}
                          >
                            <MenuItem value="" disabled>
                              <em>Select Model</em>
                            </MenuItem>
                            {config.llm === 'chatgpt' ? [
                              <MenuItem key="gpt-5" value="gpt-5">GPT-5</MenuItem>,
                              <MenuItem key="gpt-4o" value="gpt-4o">GPT-4o</MenuItem>,
                              <MenuItem key="gpt-4.1" value="gpt-4.1">GPT-4.1</MenuItem>,
                              <MenuItem key="gpt-4" value="gpt-4">GPT-4</MenuItem>,
                              <MenuItem key="gpt-4.1-mini" value="gpt-4.1-mini">GPT-4.1-mini</MenuItem>,
                              <MenuItem key="gpt-5-nano" value="gpt-5-nano">GPT-5 nano</MenuItem>,
                              <MenuItem key="gpt-5-mini" value="gpt-5-mini">GPT-5 mini</MenuItem>,
                              <MenuItem key="gpt-4.1-nano" value="gpt-4.1-nano">GPT-4.1 nano</MenuItem>
                            ] : config.llm === 'claude' ? [
                              <MenuItem key="claude-opus-4-1-20250805" value="claude-opus-4-1-20250805">Claude Opus 4.1</MenuItem>,
                              <MenuItem key="claude-opus-4-1" value="claude-opus-4-1">Claude Opus 4.1 (alias)</MenuItem>,
                              <MenuItem key="claude-opus-4-20250514" value="claude-opus-4-20250514">Claude Opus 4</MenuItem>,
                              <MenuItem key="claude-sonnet-4-5-20250929" value="claude-sonnet-4-5-20250929">Claude Sonnet 4.5</MenuItem>,
                              <MenuItem key="claude-sonnet-4-5" value="claude-sonnet-4-5">Claude Sonnet 4.5 (alias)</MenuItem>,
                              <MenuItem key="claude-3-7-sonnet-20250219" value="claude-3-7-sonnet-20250219">Claude 3.7 Sonnet</MenuItem>,
                              <MenuItem key="claude-3-5-haiku-20241022" value="claude-3-5-haiku-20241022">Claude 3.5 Haiku</MenuItem>,
                              <MenuItem key="claude-3-5-sonnet-20241022" value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet</MenuItem>
                            ] : [
                              <MenuItem key="gemini-2.5-pro" value="gemini-2.5-pro">Gemini 2.5 Pro</MenuItem>,
                              <MenuItem key="gemini-2.5-flash" value="gemini-2.5-flash">Gemini 2.5 Flash</MenuItem>,
                              <MenuItem key="gemini-2.5-flash-lite" value="gemini-2.5-flash-lite">Gemini 2.5 Flash-Lite</MenuItem>
                            ]}
                          </Select>
                        </FormControl>
                      </Box>

                      {/* Temperature Column */}
                      <Box sx={{ p: 2, borderRight: `1px solid ${theme.borderColor}` }}>
                        <FormControl fullWidth size="small">
                          <Select
                            value={config.temperature}
                            onChange={(e) => updateModelConfig(config.id, 'temperature', e.target.value)}
                            sx={{
                              color: theme.textColor,
                              fontSize: '0.9rem',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.borderColor,
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.primaryColor,
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.primaryColor,
                              },
                            }}
                          >
                            <MenuItem value="0.1">0.1</MenuItem>
                            <MenuItem value="0.3">0.3</MenuItem>
                            <MenuItem value="0.5">0.5</MenuItem>
                            <MenuItem value="0.7">0.7</MenuItem>
                            <MenuItem value="1.0">1.0</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      {/* Deploy Button Column */}
                      <Box sx={{ p: 2, borderRight: `1px solid ${theme.borderColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setLlm(config.llm);
                            setModel(config.model);
                            setTemperature(config.temperature);
                            handleUseModelOnProd();
                          }}
                          disabled={!config.model || isRunning}
                          startIcon={<PublishIcon />}
                          sx={{
                            borderColor: theme.primaryColor,
                            color: theme.primaryColor,
                            fontSize: '0.75rem',
                            py: 0.5,
                            px: 1,
                            minWidth: 'auto',
                            '&:hover': {
                              borderColor: theme.primaryColor,
                              background: `${theme.primaryColor}10`,
                            },
                            '&:disabled': {
                              borderColor: theme.textSecondaryColor,
                              color: theme.textSecondaryColor,
                            },
                          }}
                        >
                          Deploy
                        </Button>
                      </Box>

                      {/* Remove Button Column */}
                      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {modelConfigs.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => removeModelConfig(config.id)}
                            sx={{ 
                              color: theme.textSecondaryColor,
                              '&:hover': {
                                color: theme.primaryColor,
                                background: `${theme.primaryColor}10`
                              }
                            }}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      {/* Running indicator */}
                      {config.isRunning && (
                        <Box sx={{ 
                          position: 'absolute', 
                          top: 8, 
                          right: 8,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          background: 'white',
                          padding: '2px 6px',
                          borderRadius: 1,
                          border: `1px solid ${theme.borderColor}`,
                          zIndex: 1
                        }}>
                          <Box sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: theme.primaryColor,
                            animation: 'pulse 1.5s infinite'
                          }} />
                          <Typography variant="caption" sx={{ color: theme.primaryColor, fontSize: '0.7rem' }}>
                            Regenerating...
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          ) : (
            // Single model configuration for discovery mode
            <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2.2}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: theme.textSecondaryColor }}>LLM</InputLabel>
                <Select
                  value={llm}
                  onChange={(e) => {
                    setLlm(e.target.value);
                    setModel('');
                  }}
                  size="small"
                  sx={{
                    color: theme.textColor,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.borderColor,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.primaryColor,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.primaryColor,
                    },
                  }}
                >
                  <MenuItem value="chatgpt">ChatGPT</MenuItem>
                  <MenuItem value="gemini">Gemini</MenuItem>
                  <MenuItem value="claude">Claude</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <Select
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  size="small"
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: theme.textSecondaryColor }}>Select Model</span>;
                    }
                    return selected;
                  }}
                  sx={{
                    color: theme.textColor,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.borderColor,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.primaryColor,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.primaryColor,
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Select Model</em>
                  </MenuItem>
                  {llm === 'chatgpt' ? [
                    <MenuItem key="gpt-5" value="gpt-5">GPT-5</MenuItem>,
                    <MenuItem key="gpt-4o" value="gpt-4o">GPT-4o</MenuItem>,
                    <MenuItem key="gpt-4.1" value="gpt-4.1">GPT-4.1</MenuItem>,
                    <MenuItem key="gpt-4" value="gpt-4">GPT-4</MenuItem>,
                    <MenuItem key="gpt-4.1-mini" value="gpt-4.1-mini">GPT-4.1-mini</MenuItem>,
                    <MenuItem key="gpt-5-nano" value="gpt-5-nano">GPT-5 nano</MenuItem>,
                    <MenuItem key="gpt-5-mini" value="gpt-5-mini">GPT-5 mini</MenuItem>,
                    <MenuItem key="gpt-4.1-nano" value="gpt-4.1-nano">GPT-4.1 nano</MenuItem>
                  ] : llm === 'claude' ? [
                    <MenuItem key="claude-opus-4-1-20250805" value="claude-opus-4-1-20250805">Claude Opus 4.1 (2025-08-05)</MenuItem>,
                    <MenuItem key="claude-opus-4-1" value="claude-opus-4-1">Claude Opus 4.1 (alias)</MenuItem>,
                    <MenuItem key="claude-opus-4-20250514" value="claude-opus-4-20250514">Claude Opus 4 (2025-05-14)</MenuItem>,
                    <MenuItem key="claude-opus-4-0" value="claude-opus-4-0">Claude Opus 4.0 (alias)</MenuItem>,
                    <MenuItem key="claude-sonnet-4-5-20250929" value="claude-sonnet-4-5-20250929">Claude Sonnet 4.5 (2025-09-29)</MenuItem>,
                    <MenuItem key="claude-sonnet-4-5" value="claude-sonnet-4-5">Claude Sonnet 4.5 (alias)</MenuItem>,
                    <MenuItem key="claude-sonnet-4-20250514" value="claude-sonnet-4-20250514">Claude Sonnet 4 (2025-05-14)</MenuItem>,
                    <MenuItem key="claude-sonnet-4-0" value="claude-sonnet-4-0">Claude Sonnet 4.0 (alias)</MenuItem>,
                    <MenuItem key="claude-3-7-sonnet-20250219" value="claude-3-7-sonnet-20250219">Claude 3.7 Sonnet (2025-02-19)</MenuItem>,
                    <MenuItem key="claude-3-7-sonnet-latest" value="claude-3-7-sonnet-latest">Claude 3.7 Sonnet (latest)</MenuItem>,
                    <MenuItem key="claude-3-5-haiku-20241022" value="claude-3-5-haiku-20241022">Claude 3.5 Haiku (2024-10-22)</MenuItem>,
                    <MenuItem key="claude-3-5-haiku-latest" value="claude-3-5-haiku-latest">Claude 3.5 Haiku (latest)</MenuItem>,
                    <MenuItem key="claude-3-5-sonnet-20241022" value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet (2024-10-22)</MenuItem>,
                    <MenuItem key="claude-3-5-haiku-20241022" value="claude-3-5-haiku-20241022">Claude 3.5 Haiku (2024-10-22)</MenuItem>,
                    <MenuItem key="claude-3-opus-20240229" value="claude-3-opus-20240229">Claude 3 Opus (2024-02-29)</MenuItem>,
                    <MenuItem key="claude-3-opus-20240229" value="claude-3-opus-20240229">Claude 3 Opus (2024-02-29) - deprecated</MenuItem>,
                    <MenuItem key="claude-3-opus-latest" value="claude-3-opus-latest">Claude 3 Opus (latest)</MenuItem>,
                    <MenuItem key="claude-3-haiku-20240307" value="claude-3-haiku-20240307">Claude 3 Haiku (2024-03-07)</MenuItem>
                  ] : [
                    <MenuItem key="gemini-2.5-pro" value="gemini-2.5-pro">Gemini 2.5 Pro</MenuItem>,
                    <MenuItem key="gemini-2.5-flash" value="gemini-2.5-flash">Gemini 2.5 Flash</MenuItem>,
                    <MenuItem key="gemini-2.5-flash-lite" value="gemini-2.5-flash-lite">Gemini 2.5 Flash-Lite</MenuItem>
                  ]}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2.2}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: theme.textSecondaryColor }}>Temperature</InputLabel>
                <Select
                  value={temperature}
                  onChange={(e) => setTemperature(e.target.value)}
                  size="small"
                  sx={{
                    color: theme.textColor,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.borderColor,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.primaryColor,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.primaryColor,
                    },
                  }}
                >
                  <MenuItem value="0.1">0.1</MenuItem>
                  <MenuItem value="0.3">0.3</MenuItem>
                  <MenuItem value="0.5">0.5</MenuItem>
                  <MenuItem value="0.7">0.7</MenuItem>
                  <MenuItem value="1.0">1.0</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
                <Grid item xs={12} sm={12} md={1.7}>
                  <Button
                    variant="contained"
                    onClick={isSessionActive ? stopSession : startSession}
                    disabled={isRunning || (!isSessionActive && !model)}
                    startIcon={<PlayArrowIcon />}
                    fullWidth
                    sx={{
                      background: isSessionActive ? '#d32f2f' : theme.primaryColor,
                      color: 'white',
                      fontWeight: 600,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '16px',
                      '&:hover': {
                        background: isSessionActive ? '#b71c1c' : theme.primaryColor,
                        opacity: 0.9,
                      },
                      '&:disabled': {
                        background: theme.textSecondaryColor,
                      },
                    }}
                  >
                    {isRunning ? 'PROCESSING...' : isSessionActive ? 'STOP SESSION' : 'START SESSION'}
                  </Button>
                </Grid>
                <Grid item xs={12} sm={12} md={1.7}>
                  <Button
                    variant="outlined"
                    onClick={handleUseModelOnProd}
                    disabled={isRunning || !model}
                    startIcon={<PublishIcon />}
                    fullWidth
                    sx={{
                      borderColor: theme.primaryColor,
                      color: theme.primaryColor,
                      fontWeight: 600,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '16px',
                      '&:hover': {
                        borderColor: theme.primaryColor,
                        background: `${theme.primaryColor}10`,
                      },
                      '&:disabled': {
                        borderColor: theme.textSecondaryColor,
                        color: theme.textSecondaryColor,
                      },
                    }}
                  >
                    USE THIS MODEL ON PROD
                  </Button>
                </Grid>
          </Grid>
          )}
        </CardContent>
      </Card>

      {/* Discovery Prompt Editor */}
      {testMode === 'discovery' && (
        <Card sx={{ 
          mb: 3, 
          background: theme.cardBackground,
          border: `1px solid ${theme.borderColor}`,
          boxShadow: '0 4px 24px 0 rgba(30,34,40,0.08)'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: theme.textColor 
              }}>
                Discovery Prompt
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                  variant="outlined"
                  size="small"
                  onClick={fetchDiscoveryPrompt}
                  disabled={isLoadingDiscoveryPrompt}
                    sx={{
                    borderColor: theme.primaryColor,
                    color: theme.primaryColor,
                      '&:hover': {
                      borderColor: theme.primaryColor,
                      background: `${theme.primaryColor}10`,
                      },
                      '&:disabled': {
                      borderColor: theme.textSecondaryColor,
                      color: theme.textSecondaryColor,
                      },
                    }}
                  >
                  {isLoadingDiscoveryPrompt ? 'Loading...' : 'Refresh'}
                  </Button>
                  <Button
                  variant={showDiscoveryPromptEditor ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setShowDiscoveryPromptEditor(!showDiscoveryPromptEditor)}
                    sx={{
                    background: showDiscoveryPromptEditor ? theme.primaryColor : 'transparent',
                    color: showDiscoveryPromptEditor ? 'white' : theme.primaryColor,
                      borderColor: theme.primaryColor,
                      '&:hover': {
                      background: showDiscoveryPromptEditor ? theme.primaryColor : `${theme.primaryColor}10`,
                        borderColor: theme.primaryColor,
                    },
                  }}
                >
                  {showDiscoveryPromptEditor ? 'Hide Editor' : 'Edit Prompt'}
                </Button>
                {discoveryPrompt !== originalDiscoveryPrompt && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setDiscoveryPrompt(originalDiscoveryPrompt)}
                    sx={{
                      borderColor: '#f57c00',
                      color: '#f57c00',
                      '&:hover': {
                        borderColor: '#f57c00',
                        background: '#fff3e010',
                      },
                    }}
                  >
                    Reset
                  </Button>
                )}
              </Box>
            </Box>

            {discoveryPrompt !== originalDiscoveryPrompt && (
              <Box sx={{ 
                mb: 2, 
                p: 1.5, 
                background: '#fff3e0', 
                borderRadius: 1,
                border: '1px solid #f57c00'
              }}>
                <Typography variant="body2" sx={{ color: '#f57c00', fontWeight: 500 }}>
                  ⚠ Prompt has been modified. This custom prompt will be sent with your next message.
                </Typography>
              </Box>
            )}

            {showDiscoveryPromptEditor ? (
              <TextField
                fullWidth
                multiline
                rows={12}
                value={discoveryPrompt}
                onChange={(e) => setDiscoveryPrompt(e.target.value)}
                placeholder="Discovery prompt will appear here..."
                variant="outlined"
                disabled={isLoadingDiscoveryPrompt}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    color: theme.textColor,
                    '& fieldset': {
                      borderColor: theme.borderColor,
                    },
                    '&:hover fieldset': {
                      borderColor: theme.primaryColor,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.primaryColor,
                    },
                  },
                }}
              />
            ) : (
              <Box sx={{ 
                p: 2, 
                background: theme.backgroundColor,
                borderRadius: 1,
                border: `1px solid ${theme.borderColor}`,
                maxHeight: 200,
                overflow: 'auto'
              }}>
                {isLoadingDiscoveryPrompt ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <Box sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: theme.primaryColor,
                      animation: 'pulse 1.5s infinite'
                    }} />
                  </Box>
                ) : discoveryPrompt ? (
                  <Typography variant="body2" sx={{ 
                    color: theme.textColor,
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    lineHeight: 1.6
                  }}>
                    {discoveryPrompt.substring(0, 500)}
                    {discoveryPrompt.length > 500 && '...'}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ color: theme.textSecondaryColor, fontStyle: 'italic' }}>
                    No prompt loaded. Click "Refresh" to load the discovery prompt.
                  </Typography>
                )}
              </Box>
            )}
        </CardContent>
      </Card>
      )}

      {/* Discovery Chat Interface */}
      {testMode === 'discovery' && (
        <Card sx={{ 
          mb: 3, 
          background: theme.cardBackground,
          border: `1px solid ${theme.borderColor}`,
          boxShadow: '0 4px 24px 0 rgba(30,34,40,0.08)',
          minHeight: 500
        }}>
          <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Chat Header */}
            <Box sx={{ 
              p: 3, 
              background: theme.backgroundColor,
              borderBottom: `1px solid ${theme.borderColor}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  color: theme.textColor,
                  mb: 1
                }}>
                  Discovery Chat - {llm.toUpperCase()} ({model} - {temperature})
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip 
                    label={`User ID: ${userId}`}
                    size="small"
                    sx={{ 
                      background: '#e3f2fd',
                      color: '#1976d2',
                      fontWeight: 500
                    }}
                  />
                  <Chip 
                    label={`Chat ID: ${testMode === 'report' ? selectedConversationId || 'Not selected' : chatId}`}
                    size="small"
                    sx={{ 
                      background: '#f3e5f5',
                      color: '#7b1fa2',
                      fontWeight: 500
                    }}
                  />
                  <Chip 
                    label={isSessionActive ? 'Session Active' : 'Session Inactive'}
                    size="small"
                    sx={{ 
                      background: isSessionActive ? '#e8f5e8' : '#ffebee',
                      color: isSessionActive ? '#2e7d32' : '#c62828',
                      fontWeight: 500
                    }}
                  />
                  {isSessionActive && sessionStartTime && (
                    <Chip 
                      label={`Started: ${sessionStartTime.toLocaleTimeString()}`}
                      size="small"
                      sx={{ 
                        background: '#fff3e0',
                        color: '#f57c00',
                        fontWeight: 500
                      }}
                    />
                  )}
                </Box>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={clearChat}
                sx={{
                  color: theme.textSecondaryColor,
                  borderColor: theme.borderColor,
                  '&:hover': {
                    borderColor: theme.primaryColor,
                    color: theme.primaryColor,
                  },
                }}
              >
                Clear Chat
              </Button>
            </Box>
            
            {/* Chat Messages */}
            <Box sx={{ 
              flex: 1, 
              p: 3, 
              overflowY: 'auto',
              maxHeight: 400,
              minHeight: 300
            }}>
              {chatMessages.length === 0 ? (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '100%',
                  textAlign: 'center'
                }}>
                  <Typography variant="body1" sx={{ 
                    color: theme.textSecondaryColor
                  }}>
                    {isSessionActive 
                      ? "Session is active! Type a message below to continue the conversation"
                      : "Start a session by clicking 'START SESSION' to begin a new conversation"
                    }
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {chatMessages.map((message, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                        mb: 2
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: '80%',
                          p: 2,
                          borderRadius: 2,
                          background: message.role === 'user' 
                            ? theme.primaryColor 
                            : theme.backgroundColor,
                          color: message.role === 'user' 
                            ? 'white' 
                            : theme.textColor,
                          border: message.role === 'assistant' 
                            ? `1px solid ${theme.borderColor}` 
                            : 'none',
                          boxShadow: message.role === 'assistant' 
                            ? '0 2px 8px rgba(0,0,0,0.1)' 
                            : 'none'
                        }}
                      >
                        <Typography variant="body2" sx={{ 
                          whiteSpace: 'pre-wrap',
                          lineHeight: 1.5
                        }}>
                          {message.content}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                  {isRunning && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <Box sx={{
                        p: 2,
                        borderRadius: 2,
                        background: theme.backgroundColor,
                        border: `1px solid ${theme.borderColor}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <Box sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: theme.primaryColor,
                          animation: 'pulse 1.5s infinite'
                        }} />
                        <Typography variant="body2" sx={{ color: theme.textSecondaryColor }}>
                          AI is typing...
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
            
            {/* Chat Input */}
            <Box sx={{ 
              p: 3, 
              borderTop: `1px solid ${theme.borderColor}`,
              background: theme.backgroundColor
            }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  placeholder={isSessionActive ? "Type your message here..." : "Start a session to begin chatting"}
                  variant="outlined"
                  size="small"
                  disabled={!isSessionActive}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && isSessionActive) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: theme.textColor,
                      '& fieldset': {
                        borderColor: theme.borderColor,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.primaryColor,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.primaryColor,
                      },
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSendMessage}
                  disabled={!currentMessage.trim() || isRunning || !isSessionActive}
                  sx={{
                    background: theme.primaryColor,
                    color: 'white',
                    minWidth: 100,
                    height: 40,
                    '&:hover': {
                      background: theme.primaryColor,
                      opacity: 0.9,
                    },
                    '&:disabled': {
                      background: theme.textSecondaryColor,
                    },
                  }}
                >
                  Send
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Multi-Model Test Results - Report Mode Only */}
      {testMode === 'report' && modelConfigs.some(config => config.result) && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ 
              fontWeight: 600, 
              color: theme.textColor
            }}>
              Model Comparison Results
            </Typography>
            
          </Box>
          
          <Box sx={{ 
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: theme.backgroundColor,
            },
            '&::-webkit-scrollbar-thumb': {
              background: theme.borderColor,
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: theme.textSecondaryColor,
            }
          }}>
            <Grid container spacing={2} sx={{ flexDirection: 'row', flexWrap: 'nowrap', minWidth: 'fit-content' }}>
              {modelConfigs.map((config, index) => (
                config.result && (
                  <Grid item sx={{ minWidth: '300px', maxWidth: '400px', flex: '0 0 auto' }} key={config.id}>
                  <Card sx={{ 
                    background: theme.cardBackground,
                    border: `1px solid ${theme.borderColor}`,
                    boxShadow: '0 4px 24px 0 rgba(30,34,40,0.08)',
                    height: '600px',
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: '300px',
                    maxWidth: '400px'
                  }}>
                    {/* Result Header */}
                    <Box sx={{ 
                      p: 2, 
                      background: theme.backgroundColor,
                      borderBottom: `1px solid ${theme.borderColor}`
                    }}>
                       <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                         <Box sx={{ flex: 1 }}>
                           <Typography variant="h6" sx={{ 
                             fontWeight: 600, 
                             color: theme.textColor,
                             fontSize: '0.9rem',
                             textAlign: 'center'
                           }}>
                             {config.result.llmType?.toUpperCase()} ({config.result.model} - {config.result.temperature})
                           </Typography>
                         </Box>
                         <Box sx={{ display: 'flex', gap: 1 }}>
                           <Tooltip title="Copy HTML content">
                             <IconButton 
                               onClick={() => copyToClipboard(config.result.htmlContent || config.result.message)}
                               sx={{ color: theme.textSecondaryColor }}
                               size="small"
                             >
                               <ContentCopyIcon fontSize="small" />
                             </IconButton>
                           </Tooltip>
                           <Tooltip title="Regenerate this model only">
                             <IconButton 
                               onClick={() => {
                                 console.log('Regenerate button clicked for config:', config.id);
                                 console.log('Config details:', config);
                                 console.log('Selected conversation ID:', selectedConversationId);
                                 console.log('Email:', email);
                                 runSingleModelTest(config.id);
                               }}
                               disabled={config.isRunning || !selectedConversationId || !email}
                               sx={{ 
                                 color: config.result.error ? '#f57c00' : theme.primaryColor,
                                 '&:hover': {
                                   background: config.result.error ? '#fff3e010' : `${theme.primaryColor}10`
                                 },
                                 '&:disabled': {
                                   color: theme.textSecondaryColor
                                 }
                               }}
                               size="small"
                             >
                               <PlayArrowIcon fontSize="small" />
                             </IconButton>
                           </Tooltip>
                           {config.result.error && (
                             <Tooltip title="Retry this model">
                               <IconButton 
                                 onClick={() => retryModel(config.id)}
                                 disabled={config.isRunning || !selectedConversationId || !email}
                                 sx={{ 
                                   color: '#ff5722',
                                   '&:hover': {
                                     background: '#ff572210'
                                   },
                                   '&:disabled': {
                                     color: theme.textSecondaryColor
                                   }
                                 }}
                                 size="small"
                               >
                                 <RefreshIcon fontSize="small" />
                               </IconButton>
                             </Tooltip>
                           )}
                           {config.result.error && (
                             <Tooltip title="Debug: Test with minimal request">
                               <IconButton 
                                 onClick={async () => {
                                   console.log('Debug: Testing with minimal request...');
                                   const minimalRequest = {
                                     conversation_id: selectedConversationId,
                                     llm_type: config.llm,
                                     model: config.model,
                                     temperature: parseFloat(config.temperature)
                                   };
                                   console.log('Minimal request body:', minimalRequest);
                                   
                                   try {
                                     const response = await fetch('https://api.uxhq.com/generate_report_by_conversation', {
                                       method: 'POST',
                                       headers: { 'Content-Type': 'application/json' },
                                       body: JSON.stringify(minimalRequest)
                                     });
                                     console.log('Response status:', response.status);
                                     const responseText = await response.text();
                                     console.log('Response body:', responseText);
                                   } catch (error) {
                                     console.error('Debug request failed:', error);
                                   }
                                 }}
                                 sx={{ 
                                   color: '#ff9800',
                                   fontSize: '0.7rem',
                                   '&:hover': { background: '#fff3e010' }
                                 }}
                                 size="small"
                               >
                                 🐛
                               </IconButton>
                             </Tooltip>
                           )}
                         </Box>
                       </Box>
                      
                      {/* Token and Cost Info */}
                      {!config.result.error && config.result.inputTokens > 0 && (() => {
                        const costs = calculateCost(config.result.model, config.result.llmType, config.result.inputTokens, config.result.outputTokens);
                        return (
                          <Box sx={{ textAlign: 'center', mt: 1 }}>
                            <Typography variant="body2" sx={{ 
                              color: theme.textSecondaryColor, 
                              fontSize: '0.75rem',
                              mb: 0.5
                            }}>
                              <strong>Tokens:</strong> Input ({config.result.inputTokens.toLocaleString()}) / Output ({config.result.outputTokens.toLocaleString()})
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              color: theme.textSecondaryColor, 
                              fontSize: '0.75rem'
                            }}>
                              <strong>Cost Estimate:</strong> ({formatCost(costs.inputCost)} / {formatCost(costs.outputCost)}) <strong style={{ color: theme.textColor }}>{formatCost(costs.totalCost)}</strong>
                            </Typography>
                          </Box>
                        );
                      })()}
                    </Box>
                    
                    {/* Response Content */}
                    <Box sx={{ 
                      p: 2, 
                      flex: 1, 
                      overflow: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: theme.backgroundColor,
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: theme.borderColor,
                        borderRadius: '3px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: theme.textSecondaryColor,
                      }
                    }}>
                      {config.result.error ? (
                        <Box sx={{ 
                          p: 2, 
                          background: '#ffebee', 
                          borderRadius: 2, 
                          border: '1px solid #f44336' 
                        }}>
                          <Typography variant="h6" sx={{ color: '#d32f2f', mb: 1, fontSize: '0.9rem' }}>
                            Error
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#d32f2f', fontSize: '0.8rem' }}>
                            {config.result.message}
                          </Typography>
                        </Box>
                      ) : config.result.htmlContent ? (
                        <Box 
                          sx={{ 
                            width: '100%',
                            fontSize: '0.8rem',
                            lineHeight: 1.5,
                            '& *': {
                              maxWidth: '100%',
                              fontSize: 'inherit',
                              wordBreak: 'break-word'
                            },
                            '& br': {
                              display: 'block',
                              margin: '0.3em 0',
                              lineHeight: '1.2em'
                            },
                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                              fontSize: '0.9rem',
                              margin: '0.5em 0',
                              fontWeight: 600
                            },
                            '& p': {
                              margin: '0.3em 0',
                              lineHeight: '1.4'
                            }
                          }}
                          dangerouslySetInnerHTML={{ 
                            __html: config.result.htmlContent 
                          }}
                        />
                      ) : (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: theme.textColor,
                            lineHeight: 1.4,
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            wordBreak: 'break-word'
                          }}
                        >
                          {config.result.message}
                        </Typography>
                      )}
                    </Box>
                  </Card>
                </Grid>
              )
            ))}
            </Grid>
          </Box>
        </Box>
      )}

      {/* Single Model Test Results - Report Mode Only (Legacy) */}
      {testMode === 'report' && testResult && !modelConfigs.some(config => config.result) && (
        <Card sx={{ 
          background: theme.cardBackground,
          border: `1px solid ${theme.borderColor}`,
          boxShadow: '0 4px 24px 0 rgba(30,34,40,0.08)'
        }}>
          <CardContent sx={{ p: 0 }}>
            {/* Result Header */}
            <Box sx={{ 
              p: 3, 
              background: theme.backgroundColor,
              borderBottom: `1px solid ${theme.borderColor}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  color: theme.textColor,
                  mb: 1
                }}>
                  {testResult.llmType?.toUpperCase()} ({testResult.model} - {testResult.temperature})
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Chip 
                    label={`Conversation ID: ${testResult.conversationId}`}
                    size="small"
                    sx={{ 
                      background: '#e3f2fd',
                      color: '#1976d2',
                      fontWeight: 500
                    }}
                  />
                  <Chip 
                    label={`Status: ${testResult.message}`}
                    size="small"
                    sx={{ 
                      background: '#f3e5f5',
                      color: '#7b1fa2',
                      fontWeight: 500
                    }}
                  />
                </Box>
              </Box>
              <Tooltip title="Copy HTML content">
                <IconButton 
                  onClick={() => copyToClipboard(testResult.htmlContent || testResult.message)}
                  sx={{ color: theme.textSecondaryColor }}
                >
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
            
            {/* Response Content */}
            <Box sx={{ p: 3 }}>
              {testResult.error ? (
                <Box sx={{ 
                  p: 3, 
                  background: '#ffebee', 
                  borderRadius: 2, 
                  border: '1px solid #f44336' 
                }}>
                  <Typography variant="h6" sx={{ color: '#d32f2f', mb: 1 }}>
                    Error
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#d32f2f' }}>
                    {testResult.message}
                  </Typography>
                </Box>
              ) : testResult.htmlContent ? (
                <Box 
                  sx={{ 
                    width: '100%',
                    '& *': {
                      maxWidth: '100%'
                    },
                    '& br': {
                      display: 'block',
                      margin: '0.5em 0',
                      lineHeight: '1.2em'
                    }
                  }}
                  dangerouslySetInnerHTML={{ 
                    __html: testResult.htmlContent 
                  }}
                />
              ) : (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: theme.textColor,
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: '14px'
                  }}
                >
                  {testResult.message}
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Empty State - Report Mode Only */}
      {testMode === 'report' && !testResult && !isRunning && !modelConfigs.some(config => config.result) && !isMultiModelRunning && (
        <Card sx={{ 
          background: theme.cardBackground,
          border: `1px solid ${theme.borderColor}`,
          boxShadow: '0 4px 24px 0 rgba(30,34,40,0.08)',
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="h6" sx={{ 
              color: theme.textSecondaryColor,
              mb: 2
            }}>
              No test results yet
            </Typography>
            <Typography variant="body2" sx={{ 
              color: theme.textSecondaryColor
            }}>
              Configure your model parameters above and click "COMPARE MODELS" to see results
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Loading State - Multi-Model Report Mode */}
      {testMode === 'report' && isMultiModelRunning && (
        <Card sx={{ 
          background: theme.cardBackground,
          border: `1px solid ${theme.borderColor}`,
          boxShadow: '0 4px 24px 0 rgba(30,34,40,0.08)',
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: '50%', 
              background: '#e3f2fd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              animation: 'pulse 2s infinite'
            }}>
              <CompareArrowsIcon sx={{ color: '#1976d2' }} />
            </Box>
            <Typography variant="h6" sx={{ 
              color: theme.textColor,
              mb: 1
            }}>
              Comparing Models...
            </Typography>
            <Typography variant="body2" sx={{ 
              color: theme.textSecondaryColor
            }}>
              Running tests for {modelConfigs.filter(c => c.model).length} model(s) in parallel
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Loading State - Single Model Report Mode (Legacy) */}
      {testMode === 'report' && isRunning && !isMultiModelRunning && (
        <Card sx={{ 
          background: theme.cardBackground,
          border: `1px solid ${theme.borderColor}`,
          boxShadow: '0 4px 24px 0 rgba(30,34,40,0.08)',
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: '50%', 
              background: '#e3f2fd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              animation: 'pulse 2s infinite'
            }}>
              <PlayArrowIcon sx={{ color: '#1976d2' }} />
            </Box>
            <Typography variant="h6" sx={{ 
              color: theme.textColor,
              mb: 1
            }}>
              Running Test...
            </Typography>
            <Typography variant="body2" sx={{ 
              color: theme.textSecondaryColor
            }}>
              Please wait while we process your request
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Deployment History */}
      <Card sx={{ 
        mb: 3, 
        background: theme.cardBackground,
        border: `1px solid ${theme.borderColor}`,
        boxShadow: '0 4px 24px 0 rgba(30,34,40,0.08)'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 600, 
              color: theme.textColor
            }}>
              Deployment History
            </Typography>
            {deploymentHistory.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const dataStr = JSON.stringify(deploymentHistory, null, 2);
                  const dataBlob = new Blob([dataStr], {type: 'application/json'});
                  const url = URL.createObjectURL(dataBlob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = 'deployment-history.json';
                  link.click();
                  URL.revokeObjectURL(url);
                }}
                sx={{
                  borderColor: theme.primaryColor,
                  color: theme.primaryColor,
                  '&:hover': {
                    borderColor: theme.primaryColor,
                    background: `${theme.primaryColor}10`,
                  },
                }}
              >
                Export JSON
              </Button>
            )}
          </Box>
          
          {deploymentHistory.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4,
              color: theme.textSecondaryColor
            }}>
              <Typography variant="body1">
                No deployments yet. Deploy a model configuration to see history here.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {deploymentHistory.map((entry, index) => (
                <Box
                  key={entry.id}
                  sx={{
                    p: 2,
                    background: theme.backgroundColor,
                    borderRadius: 2,
                    border: `1px solid ${theme.borderColor}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Chip
                      label={entry.type.toUpperCase()}
                      size="small"
                      sx={{
                        background: entry.type === 'discovery' ? '#1976d2' : '#7b1fa2',
                        color: 'white',
                        fontWeight: 600
                      }}
                    />
                    <Typography variant="body2" sx={{ color: theme.textColor, fontWeight: 500 }}>
                      {entry.llm.toUpperCase()} - {entry.model}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.textSecondaryColor }}>
                      Temp: {entry.temperature}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.textSecondaryColor }}>
                      {entry.date} at {entry.time}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: theme.textSecondaryColor }}>
                      #{index + 1}
                    </Typography>
                    <Chip
                      label={entry.filename}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: theme.borderColor,
                        color: theme.textSecondaryColor,
                        fontSize: '0.7rem'
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Production Deployment Confirmation Dialog */}
      <Dialog
        open={showProdDialog}
        onClose={handleDiscardProd}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            background: theme.cardBackground,
            border: `1px solid ${theme.borderColor}`,
          }
        }}
      >
        <DialogTitle sx={{ 
          color: theme.textColor,
          fontWeight: 600,
          fontSize: '20px',
          textAlign: 'center',
          pb: 1
        }}>
          Deploy to Production
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 2 }}>
          <Typography 
            variant="body1" 
            sx={{ 
              color: theme.textSecondaryColor,
              mb: 2,
              fontSize: '16px'
            }}
          >
            Are you sure you want to deploy this model configuration to production?
          </Typography>
          <Box sx={{ 
            background: theme.backgroundSecondary,
            borderRadius: 2,
            p: 2,
            mb: 2,
            border: `1px solid ${theme.borderColor}`
          }}>
            <Typography variant="body2" sx={{ color: theme.textColor, fontWeight: 500, mb: 1 }}>
              Model Configuration:
            </Typography>
            <Typography variant="body2" sx={{ color: theme.textSecondaryColor }}>
              LLM: <strong>{llm}</strong> | Model: <strong>{model}</strong> | Temperature: <strong>{temperature}</strong>
            </Typography>
          </Box>
          <Typography 
            variant="body2" 
            sx={{ 
              color: theme.textSecondaryColor,
              fontSize: '14px'
            }}
          >
            This action will affect all users in production.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          justifyContent: 'center', 
          gap: 2, 
          px: 3, 
          pb: 3,
          pt: 1
        }}>
          <Button
            onClick={handleDiscardProd}
            variant="outlined"
            sx={{
              borderColor: theme.textSecondaryColor,
              color: theme.textSecondaryColor,
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': {
                borderColor: theme.textSecondaryColor,
                background: `${theme.textSecondaryColor}10`,
              },
            }}
          >
            Discard
          </Button>
          <Button
            onClick={handleConfirmProd}
            variant="contained"
            disabled={isRunning}
            sx={{
              background: theme.primaryColor,
              color: 'white',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': {
                background: theme.primaryColor,
                opacity: 0.9,
              },
              '&:disabled': {
                background: theme.textSecondaryColor,
              },
            }}
          >
            {isRunning ? 'SAVING...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
